/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Oct 11 2023
 **/

import styled from "@emotion/styled";
import { Theme, css } from "@emotion/react";

import { Column, Row, Typography } from "@/frameworks/designStuff/components";

const flashAnimation = (theme: Theme) => css`
	@keyframes flash {
		0%,
		100% {
			background-color: none;
		}
		50% {
			background-color: ${theme.colors.accent_20_100};
		}
	}
`;

export const ChatWrapper = styled(Column.li)<{
	status: "failed" | "success";
	variant: ChatUI.Size;
	emergency: boolean;
}>`
	border-radius: ${({ variant }) => {
		switch (variant) {
			case "miniPopup": {
				return;
			}
			case "fullSize": {
				return "12px";
			}
			case "transparent": {
				return "12px";
			}
		}
	}};
	width: -webkit-fill-available;
	${({ theme }) => flashAnimation(theme)}
	&.flash-animation {
		animation: flash 2s ease-in-out;
	}
`;

export const Blur = styled(Column)<{ status: "failed" | "success"; variant: ChatUI.Size; emergency: boolean }>`
	width: 100%;
	height: 100%;
	padding: ${({ variant }) => (variant === "transparent" ? "8px 16px" : "0px 16px")};
	padding-bottom: ${({ variant }) => (variant !== "transparent" ? "4px" : undefined)};
	border-radius: 12px;
	backdrop-filter: ${({ variant }) => variant === "transparent" && "blur(10px)"};
	background: ${({ theme, status, emergency, variant }) => {
		if (status === "success") {
			if (emergency) return theme.colors.warning_00_100;
			if (variant !== "transparent") return "none";
			return theme.colors.neutral_00_80;
		} else {
			return theme.colors.deco1_10_100;
		}
	}};
`;

export const Message = styled(Column)<{ hover: boolean }>`
	width: -webkit-fill-available;
	position: relative;
	margin-left: 38px;
	padding: 1px 4px;
	border-radius: 4px;
	&:hover {
		background: ${({ hover, theme }) => (hover ? theme.colors.secondary_80_16 : "none")};
		display: inline-flex;
		.chat-icons {
			display: flex;
		}
	}
`;

export const Date = styled(Typography)`
	color: ${({ theme }) => theme.colors.neutral_60_100};
`;

export const Sender = styled(Typography.p)`
	max-width: 130px;
	overflow: hidden;
	text-overflow: ellipsis;
	word-break: break-all;
	white-space: nowrap;
`;

export const HoverIconArea = styled(Row)`
	display: none;
	position: absolute;
	bottom: 100%;
	right: 0;
	transform: translateY(4px);
	background: ${({ theme }) => theme.colors.neutral_00_100};
	backdrop-filter: blur(10px);
	border-radius: 12px;
	box-shadow: 12px 0px 20px 0px rgba(0, 0, 0, 0.16);
	padding: 5px 6px;
`;

export const ThreadBox = styled(Row)`
	width: -webkit-fill-available;
	margin-left: 38px;
	padding: 4px 0;
	margin-top: 4px;
`;

export const ThreadNumberBox = styled.div`
	width: fit-content;
	cursor: pointer;
	border-radius: 20px;
	padding: 4px 8px;
	background-color: ${({ theme }) => theme.colors.accent_00_100};

	:hover {
		background-color: ${({ theme }) => theme.colors.accent_10_100};
	}
	:active {
		background-color: ${({ theme }) => theme.colors.accent_20_100};
	}
`;
